import { SelfImprovement as WellBeingIcon } from "@mui/icons-material";
import { Box, CircularProgress } from "@mui/material";
import { activityApi } from "apis";
import MonthPickerCalendar from "components/common/MonthPickerCalendar";
import DailyWellBeingScoreChart, {
  WellBeingScore,
} from "components/features/patient/detail/DailyWellBeingScoreChart";
import {
  endOfTheMonth,
  formatDate,
  startOfTheMonth,
} from "helpers/dateTimeHelper";
import {
  MetricValuesAggregationRequestDtoAggregateTypesEnum,
  MetricValuesAggregationRequestDtoMetricCodeEnum,
  MetricValuesAggregationRequestDtoIntervalEnum,
  MetricValuesAggregationRequestDtoOrderByEnum,
  MetricValuesAggregationResponseDto,
} from "libs/cellera-sdk";
import { isEmpty, isNil } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import DashboardLayout from "../components/layouts/DashboardLayout";
import PatientDetailPageContainer from "./components/PatientDetailPageContainer";

const DailyWellBeingScorePage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const { lastMonthChecked } = useParams<{ lastMonthChecked: string }>();
  const { lastYearChecked } = useParams<{ lastYearChecked: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<number>(
    lastMonthChecked ? Number(lastMonthChecked) : new Date().getMonth() + 1
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    lastYearChecked ? Number(lastYearChecked) : new Date().getFullYear()
  );
  const [startDate, setStartDate] = useState<string>(
    startOfTheMonth(selectedYear, selectedMonth).toISOString()
  );
  const [endDate, setEndDate] = useState<string>(
    endOfTheMonth(selectedYear, selectedMonth).toISOString()
  );
  const [data, setData] = useState<WellBeingScore[]>([]);

  const navigate = useNavigate();

  const handleDateChange = useCallback((month: number, year: number) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    setStartDate(startOfTheMonth(year, month).toISOString());
    setEndDate(endOfTheMonth(year, month).toISOString());
  }, []);

  if (!patientId) {
    return (
      <Box sx={{ p: 2 }}>
        <div>Patient ID not found</div>
      </Box>
    );
  }
  const fetchData = useCallback(
    async (
      patientId: string | undefined,
      startDate: string,
      endDate: string
    ): Promise<MetricValuesAggregationResponseDto> => {
      if (isNil(patientId) || isEmpty(patientId)) {
        return { data: [], totalIntervals: 0 };
      }
      const response = await activityApi.getMetricValuesAggregatedByPatientId({
        patientId,
        metricValuesAggregationRequestDto: {
          interval: MetricValuesAggregationRequestDtoIntervalEnum.Daily,
          aggregateTypes: [
            MetricValuesAggregationRequestDtoAggregateTypesEnum.LatestRecord,
          ],
          metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.WellBeing,
          orderBy: MetricValuesAggregationRequestDtoOrderByEnum.Desc,
          startDate: startDate,
          endDate: endDate,
        },
      });
      return response;
    },
    []
  );

  useEffect(() => {
    const fetchAndSetData = async () => {
      setIsLoading(true);
      try {
        const response = await fetchData(patientId, startDate, endDate);
        setData(
          response.data.map((item) => ({
            date: item.startDate,
            value: item.valueIntData?.latest ?? 0,
            formattedDate: formatDate(item.startDate, "MMM DD, YYYY"),
          }))
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchAndSetData();
  }, [fetchData, patientId, startDate, endDate]);

  const handleRefresh = useCallback(() => {
    const fetchAndSetData = async () => {
      setIsLoading(true);
      try {
        const response = await fetchData(patientId, startDate, endDate);
        setData(
          response.data.map((item) => ({
            date: item.startDate,
            value: item.valueIntData?.latest ?? 0,
            formattedDate: item.startDate,
          }))
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchAndSetData();
  }, [fetchData, patientId, startDate, endDate]);

  return (
    <DashboardLayout title="Daily Wellbeing Score">
      <PatientDetailPageContainer
        breadcrumbs={[
          { label: "Dashboard", href: "/dashboard", order: 0 },
          { label: "Patient Management", href: "/patients", order: 1 },
          { label: "Patient Details", href: `/patients`, order: 2 },
        ]}
        lastBreadcrumbItem="Daily Wellbeing Score"
        title="Daily Wellbeing Score"
        icon={<WellBeingIcon />}
        onBack={() => navigate("/patients")}
        onRefresh={handleRefresh}
        rightCornerIconAction={{
          icon: (
            <MonthPickerCalendar
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              onDateChange={handleDateChange}
            />
          ),
          onClick: () => {},
          tooltip: "Select month and year",
        }}
        containerSx={{ height: "calc(100% - 300px)" }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DailyWellBeingScoreChart data={data} isLoading={isLoading} />
        )}
      </PatientDetailPageContainer>
    </DashboardLayout>
  );
};

export default DailyWellBeingScorePage;
